import PasswordField from "@/components/feature/auth/PasswordField";
import PageShell from "@/components/feature/PageShell";
import "../kayit/signup.css";
import { useSearchParams } from "react-router-dom";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getDataClient } from "@/lib/dataClient";
import { roleHomePath, translateAuthError } from "@/hooks/useAuth";
import {useSiteSettingsContext} from "@/hooks/useSiteSettingsContext";
import { hasVerifiedTotp } from "@/hooks/useMfa";
import MfaCodeForm from "@/components/feature/auth/MfaCodeForm";

export default function Giris() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const isParent = params.get("rol") === "veli";
  const [status, setStatus] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [mfa, setMfa] = useState<{ factorId: string; role: string } | null>(null);
  const { settings } = useSiteSettingsContext();
  const name = settings.siteName || "Dersdinle";

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus(null);

    const form = e.currentTarget;
    const fd = new FormData(form);
    const email = String(fd.get("email") || "").trim();
    const password = String(fd.get("password") || "");

    if (!email || !password) return;

    setLoading(true);
    try {
      const dataClient = getDataClient();
      const { data, error } = await dataClient.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        setStatus(translateAuthError(error.message));
        return;
      }

      const role = (data.user?.user_metadata?.role as string) || "ogrenci";

      // 2FA kayıtlıysa ikinci adıma geç.
      const { has, factorId } = await hasVerifiedTotp();
      if (has) {
        setMfa({ factorId, role });
        return;
      }

      navigate(roleHomePath(role as never), { replace: true });
    } catch {
      setStatus("Beklenmeyen bir hata oluştu. Lütfen tekrar deneyin.");
    } finally {
      setLoading(false);
    }
  };

  return (<PageShell crumbs={[{label:'Ana Sayfa',href:'/'},{label:isParent?'Veli Girişi':'Öğrenci Girişi'}]}><section className="signup-section"><div className="signup-card login-card"><aside className="signup-story"><span className="signup-accent"/><h2>{isParent?'Öğrenme yolculuğunda':'Kaldığın yerden'} <em>{isParent?'yanında ol.':'devam et.'}</em></h2><h3>Derslerin Sesi, Başarının Yolu...</h3><ul>{(isParent?[['ri-bar-chart-line','Öğrencinin gelişimini izle','Çalışmalarını ve ilerlemesini takip et.'],['ri-computer-line','Meraklı Masaüstü ve Mobil takibi','Öğrencine bağlı cihazları incele.'],['ri-parent-line','Öğrenme sürecine eşlik et','Kendi veli hesabından takip et.']]:[['ri-play-circle-line','Derslerine kolayca ulaş','Çalışma alanına tek yerden devam et.'],['ri-book-open-line','Sorularla bilgini pekiştir','Öğrendiklerini uygulamaya dönüştür.'],['ri-bar-chart-line','Gelişimini takip et','Sıradaki adımını belirle.']]).map(([icon,title,text])=><li key={title}><i className={icon} aria-hidden="true"/><div><strong>{title}</strong><span>{text}</span></div></li>)}</ul><div className="signup-art" aria-hidden="true"><i className={isParent?'ri-parent-line':'ri-book-open-line'}/><i className="ri-graduation-cap-line"/></div></aside><div className="signup-form login-form"><div className="signup-roles" role="group" aria-label="Giriş türü">{[['ogrenci','Öğrenci Girişi','ri-graduation-cap-line'],['veli','Veli Girişi','ri-parent-line']].map(([value,label,icon])=><button key={value} type="button" disabled={loading||!!mfa} aria-pressed={(isParent?'veli':'ogrenci')===value} onClick={()=>{setStatus(null);setParams({rol:value});}}><i className={icon} aria-hidden="true"/><strong>{label}</strong></button>)}</div><h1 className="login-title">{mfa?'İki aşamalı doğrulama':isParent?'Veli hesabına giriş yap':'Tekrar hoş geldin!'}</h1><p className="signup-subtitle">{mfa?'Girişini tamamlamak için doğrulama kodunu gir.':isParent?'Kendi veli hesabınla öğrencinin çalışmalarını takip et.':'Derslerine ve çalışma alanına ulaşmak için giriş yap.'}</p>
            {mfa ? (
              <MfaCodeForm
                factorId={mfa.factorId}
                onSuccess={() => navigate(roleHomePath(mfa.role as never), { replace: true })}
                onBack={() => setMfa(null)}
              />
            ) : (
            <form onSubmit={handleSubmit} className="mt-6 space-y-4">
              <div>
                <label
                  htmlFor="giris-email"
                  className="block text-sm font-medium text-foreground-800"
                >
                  E-posta
                </label>
                <input
                  id="giris-email"
                  name="email"
                  type="email"
                  required
                  autoComplete="email"
                  placeholder="ornek@mail.com"
                  className="mt-1.5 w-full h-11 px-3.5 rounded-md border border-background-200 bg-background-100/60 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50 focus:outline-none transition-colors"
                />
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <label
                    htmlFor="giris-sifre"
                    className="block text-sm font-medium text-foreground-800"
                  >
                    Şifre
                  </label>
                  <Link
                    to="/sifremi-unuttum"
                    className="text-xs font-medium text-primary-700 hover:text-primary-800 whitespace-nowrap"
                  >
                    Şifremi unuttum
                  </Link>
                </div>
                <PasswordField
                  id="giris-sifre"
                  name="password"
                  type="password"
                  required
                  autoComplete="current-password"
                  placeholder="••••••••"
                  className="mt-1.5 w-full h-11 px-3.5 rounded-md border border-background-200 bg-background-100/60 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50 focus:outline-none transition-colors"
                />
              </div>

              <label className="block text-sm">Doğrulama kodu (etkinleştirdiyseniz)<input name="otp" autoComplete="one-time-code" maxLength={20} className="mt-2 w-full rounded-lg border p-3" placeholder="6 haneli kod veya yedek kod" /></label>
<button
                type="submit"
                disabled={loading}
                className="signup-submit w-full"
              >
                {loading ? "Giriş yapılıyor..." : (isParent ? "Veli paneline giriş yap →" : "Öğrenci paneline giriş yap →")}
              </button>
            </form>
            )}

            {status && (
              <p role="status" className="mt-4 text-sm text-foreground-600 bg-background-100 rounded-md p-3 leading-relaxed">
                {status}
              </p>
            )}

            {settings.registrationOpen && (
              <p className="mt-6 text-sm text-center text-foreground-600">
                Hesabın yok mu?{" "}
                <Link to={"/kayit?rol="+(isParent?"veli":"ogrenci")} className="text-primary-700 font-semibold hover:text-primary-800">
                  Ücretsiz Üye Ol
                </Link>
              </p>
            )}

<div className="login-help"><strong>{isParent?'Öğrencini henüz eşleştirmedin mi?':'Girişte sorun mu yaşıyorsun?'}</strong><p>{isParent?'Giriş yaptıktan sonra eşleştirme koduyla öğrencini ekleyebilirsin.':'Hesabına erişim için Yardım Merkezi’ndeki rehberleri incele.'}</p><Link to="/yardim">Yardım Merkezi →</Link></div></div></div></section></PageShell>);
}

